import { useContext } from 'react';

import { L10nContext } from 'context/L10nContext';

import { MaintenanceLogEntryViewModel } from 'services/maintenance-log/domain/model/MaintenanceLogEntryModel';

import { useKeyPress } from 'presentation/hooks';
import { ColumnLayoutSection } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSection';
import { ColumnLayoutSectionMode } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSectionMode';
import { ColumnLayout } from 'presentation/ui/layouts/column-layout/ColumnLayout';
import { FormLayoutHeader } from 'presentation/ui/layouts/form-layout/form-layout-header/FormLayoutHeader';
import { FormLayoutSection } from 'presentation/ui/layouts/form-layout/form-layout-section/FormLayoutSection';
import { FormLayout } from 'presentation/ui/layouts/form-layout/FormLayout';
import { FormLayoutModifier } from 'presentation/ui/layouts/form-layout/FormLayoutModifier';
import { FormLayoutSectionAlign } from 'presentation/ui/layouts/form-layout/FormLayoutSectionAlign';
import { ButtonPrimary } from 'presentation/ui/partials/button/button-primary/ButtonPrimary';
import { ButtonSecondary } from 'presentation/ui/partials/button/button-secondary/ButtonSecondary';
import { IconIdentifier } from 'presentation/ui/partials/icon/IconIdentifier';
import { InputText, InputTextStatus } from 'presentation/ui/partials/input/input-text/InputText';
import { Label } from 'presentation/ui/partials/input/label/Label';
import { Textarea, TextareaStatus } from '../../../../../../presentation/ui/partials/input/textarea/Textarea';

export interface MaintenanceLogResolveFormStep2Props {
	/**
	 * Maintenance log entry
	 */
	maintenanceLogEntry: MaintenanceLogEntryViewModel;
	/**
	 * Button back action
	 */
	onClickPrevious: () => void;
	/**
	 * Button confirm
	 */
	onClickConfirm: () => void;
}

export const MaintenanceLogResolveFormStep2 = (props: MaintenanceLogResolveFormStep2Props): JSX.Element => {
	const { maintenanceLogEntry, onClickPrevious, onClickConfirm } = props;

	const l10nContext = useContext(L10nContext);
	const endDateFormatted = l10nContext.formatDateTime(maintenanceLogEntry?.IncidentDateEnd);

	useKeyPress('Enter', onClickConfirm);

	return (
		<FormLayout
			align={FormLayoutSectionAlign.ALIGN_HORIZONTAL}
			modifier={FormLayoutModifier.BLUE_BACKGROUND}
			onSubmit={onClickConfirm}
		>
			<FormLayoutHeader
				text={l10nContext.translate('common.modal.checkDetails', 'Überprüfen Sie bitte Ihre Angaben')}
				icon={IconIdentifier.WARNING}
			/>

			<FormLayoutSection>
				<Label label={`${l10nContext.translate('view.maintenances.modal.action.resolve.resolveHint', 'Achtung')}`}>
					<Textarea
						defaultValue={l10nContext.translate('view.maintenances.modal.action.resolve.resolveHintText',
							'Mit dem Setzen eines Datums wird der Betriebsbucheintrag behoben. ' +
							'Es ist dann nicht mehr möglich das Kommentarfeld zu editieren.')}
						status={TextareaStatus.READONLY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection>
				<Label
					label={l10nContext.translate('view.maintenances.modal.action.edit.resolveDate', 'Datum der Behebung')}
					thin
				>
					<InputText
						type='text'
						value={endDateFormatted}
						status={InputTextStatus.SUMMARY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection align={FormLayoutSectionAlign.ALIGN_BOTTOM}>
				<ColumnLayout>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FLEX}>
						<ButtonSecondary
							buttonText={l10nContext.translate('common.button.back', 'Zurück')}
							onClick={onClickPrevious}
						/>
					</ColumnLayoutSection>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FIXED}>
						<ButtonPrimary
							buttonText={l10nContext.translate('view.maintenances.modal.action.resolve.caption', 'Betriebsbucheintrag beheben')}
							type='submit'
						/>
					</ColumnLayoutSection>
				</ColumnLayout>
			</FormLayoutSection>
		</FormLayout>
	);
};
